import { GridOptions, PostProcessPopupParams } from 'ag-grid-community';
import React, {
  ComponentType,
  FC,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { getAbsoluteHeight } from 'utils/dom';

const fix = (ePopup: HTMLElement) => {
  const element =
    document.querySelector('.ant-picker-dropdown') ||
    document.querySelector('.ozone-select__menu');

  if (element == null) {
    return;
  }
  const y = parseInt(ePopup.style.top.replace('px', ''), 10);
  const popupParent = document.querySelector(
    '[ref=eBodyViewport]',
  ) as HTMLElement;
  const { height: parent_height } = popupParent.getBoundingClientRect();
  const { height: popup_height } = ePopup.getBoundingClientRect();
  const required_height = popup_height + element.clientHeight;
  let minimum_height = Math.min(required_height, parent_height);
  let diff = 0;

  if (ePopup.offsetHeight > 0) {
    minimum_height = ePopup!.clientHeight;
    const required_absolute_height =
      getAbsoluteHeight(ePopup) - 2 * popup_height + required_height;

    diff = required_absolute_height - minimum_height;
  }
  const maxY = parent_height - minimum_height - diff;
  const y_updated = Math.min(Math.max(y, 0), Math.abs(maxY));

  /* eslint-disable no-param-reassign */
  ePopup.style.top = `${y_updated}px`;
  /* eslint-enable no-param-reassign */
};

export type EditorPopupParams = Required<
  Pick<PostProcessPopupParams, 'column' | 'ePopup' | 'rowNode'>
>;

const _postProcessPopup = ({ ePopup }: EditorPopupParams) => {
  fix(ePopup);
  window.setTimeout(() => fix(ePopup), 30);
};

export type EditorPopupsProps = {
  onPopup?: (params: EditorPopupParams) => boolean;
};

export const withEditorPopups = <
  T extends Pick<GridOptions, 'postProcessPopup'>,
>(
  Component: ComponentType<T>,
): FC<Omit<T, 'postProcessPopup'> & EditorPopupsProps> => {
  const Next: FC<Omit<T, 'postProcessPopup'> & EditorPopupsProps> = ({
    onPopup,
    ...props
  }) => {
    const onPopupRef = useRef<Required<EditorPopupsProps>['onPopup'] | null>(
      onPopup ?? null,
    );
    const postProcessPopup = useCallback(
      (params: PostProcessPopupParams) => {
        if (params.column == null || params.rowNode == null) {
          return;
        }
        const editor_popup_params = params as EditorPopupParams;
        const handled = onPopupRef.current?.(editor_popup_params) ?? false;

        if (handled) {
          return;
        }
        _postProcessPopup(editor_popup_params);
      },
      [onPopupRef],
    );

    useEffect(() => {
      onPopupRef.current = onPopup ?? null;
    }, [onPopupRef, onPopup]);

    return (
      <Component {...(props as any)} postProcessPopup={postProcessPopup} />
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithEditorPopups'))(Next);
};
